import axios from "axios";

/**
 * Guided-tour content API (in-house Appcues replacement).
 * Steps live in the DB (screening.TutorialStep) — see TourGuide.tsx.
 */

export interface TutorialStepData {
    order: number;
    /** data-tour attribute of the on-page target; null = centered modal. */
    anchor: string | null;
    /** Raw CSS selector for the target — overrides anchor when set
     *  (admin-editable without a frontend deploy). */
    selector?: string | null;
    title: string;
    /** HTML body. */
    body: string;
    /** Optional call-to-action button (promos; admin-editable). */
    cta?: { label: string; href: string } | null;
}

export interface TutorialResponse {
    steps: TutorialStepData[];
    /** "tour" (spotlight walkthrough) or "promo" (hero modal). The
     *  global "turn off all guides" opt-out only applies to tours. */
    kind?: string;
}

export const getTutorial = async (tourId: string): Promise<TutorialResponse> => {
    const res = await axios.get(`/interface/api/tutorials/${tourId}`);
    // Step keys are all single words on purpose — immune to the GUIv=2
    // camelize round trip (tour_id would arrive as tourId, so we don't
    // read it).
    return { steps: res.data?.steps ?? [], kind: res.data?.kind };
};

export interface HelpMenuEntry {
    /** tour_id */
    id: string;
    label: string;
    /** In-app page the tour runs on ("/markets-overview?tour=welcome");
     *  null = start the tour in place on the current page. */
    href: string | null;
}

export interface AutoTourEntry {
    /** tour_id */
    id: string;
    /** Whitespace-separated Next.js route pattern(s) the tour
     *  auto-starts on, e.g. "/s/[exchange]/[ticker]/short-interest" —
     *  one show per user across every stock. */
    route: string;
}

export interface HelpMenuResponse {
    tutorials: HelpMenuEntry[];
    auto: AutoTourEntry[];
}

/** The guided tours the current user may launch from the Help menu.
 *
 *  Audience-gated server-side, so this is per-user and cannot be shared or
 *  cached across visitors. Fetched only when the Help menu opens — the page
 *  load wants `getAutoTourRoutes` instead, which is a different question with
 *  a much cheaper answer. */
export const getHelpMenu = async (): Promise<HelpMenuResponse> => {
    const res = await axios.get(`/interface/api/tutorials/`);
    return { tutorials: res.data?.tutorials ?? [], auto: res.data?.auto ?? [] };
};

/** Which tours auto-start on which route.
 *
 *  The one tutorials call the app makes on every page load, because a route
 *  change has to be able to trigger a tour. Deliberately NOT audience-filtered:
 *  it is the same answer for every visitor, so the server caches one copy and
 *  the browser keeps it. What it costs is that a user outside a tour's audience
 *  can match a route and ask for the tour — `getTutorial` re-runs the targeting
 *  server-side and hands back no steps, so nothing shows. That is one wasted
 *  request in an uncommon case, against a per-user query on every page view. */
export const getAutoTourRoutes = async (): Promise<AutoTourEntry[]> => {
    const res = await axios.get(`/interface/api/tutorials/auto/routes`);
    return res.data?.auto ?? [];
};

/** Aggregate impression counter — fire-and-forget, failure is silent. */
export const postTutorialImpression = (tourId: string, order: number): void => {
    axios.post(`/interface/api/tutorials/${tourId}/impression`, { order }).catch(() => {
        /* stats only — never block the tour */
    });
};
